import React, { useState, useEffect, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { CircularProgress } from '@mui/material'

// Important: Set the worker source for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`

type CitationViewerProps = {
  fileUrl: string
  isLoading?: boolean
}

const CitationViewer = ({
  fileUrl,
  isLoading: externalLoading,
}: CitationViewerProps) => {
  const [pageRange, setPageRange] = useState<number[]>([])
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  )
  const containerRef = useRef<HTMLDivElement>(null)

  // Measure container width for scaling PDF
  useEffect(() => {
    function updateWidth() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPageRange(Array.from({ length: numPages }, (_, i) => i + 1))
  }

  return (
    <div
      className="flex flex-col gap-2"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="max-h-[70vh] w-full min-w-0 flex-1 overflow-y-auto"
        ref={containerRef}
      >
        {externalLoading ? (
          <div className="mt-8 flex justify-center">
            <CircularProgress />
          </div>
        ) : (
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<CircularProgress />}
          >
            {pageRange.map((pageNumber) => (
              <Page
                key={pageNumber}
                pageNumber={pageNumber}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={
                  containerWidth
                    ? Math.min(Math.floor(containerWidth), 1000)
                    : undefined
                }
                className={`border-1 mx-auto border border-solid border-black bg-white ${
                  pageNumber !== pageRange[pageRange.length - 1] ? 'mb-3' : ''
                }`}
              />
            ))}
          </Document>
        )}
      </div>
    </div>
  )
}

export default CitationViewer
